/**
 * Help copy for the InfoPopover system.
 *
 * Every topic is a plain-language definition, an optional formula written in
 * standard text (Unicode operators, no LaTeX renderer is bundled), and operational
 * guidance. Formulas mirror the arithmetic the UI and domain layer actually use;
 * where the simulator does not expose a closed-form number, the entry says so.
 *
 * Topic ids are the only accepted values for `<InfoPopover topic=… />`, so a typo
 * fails at compile time.
 */

export interface InfoTopic {
  readonly title: string;
  /** One or two sentences in plain language. */
  readonly summary: string;
  /** Standard-text formula, shown in monospace. Omit when there is no closed form. */
  readonly formula?: string;
  /** Concrete actions for the player, most useful first. */
  readonly guidance: readonly string[];
}

const TOPICS = {
  // ---------------------------------------------------------------------------
  // Route Analytics Inspector
  // ---------------------------------------------------------------------------
  rasm: {
    title: 'RASM',
    summary:
      'Revenue per Available Seat Mile. How much ticket revenue each seat you offered earned, per mile flown. It rewards both filling seats and selling them at good fares.',
    formula: 'RASM = Route revenue ÷ Available Seat Miles (ASM)',
    guidance: [
      'If RASM is below CASM, the route cannot cover its costs at current fares.',
      'Raise fares on cabins with high load factor first, where demand is proven to absorb it.',
      'Remove low-fare inventory on peak departures so seats sell at a higher average fare.',
    ],
  },
  casm: {
    title: 'CASM',
    summary:
      'Cost per Available Seat Mile. What it costs to fly one seat one mile, whether or not that seat is sold. Lower is better.',
    formula: 'CASM = Operating cost ÷ Available Seat Miles (ASM)',
    guidance: [
      'Up-gauge to a larger aircraft on the same route to spread crew and fee costs over more seats.',
      'Trim cabin weight: heavier cabins burn more fuel every flight hour (see Mass Delta Fuel Burn).',
      'Raise aircraft utilization so ownership and maintenance costs are spread over more flying.',
    ],
  },
  operatingMargin: {
    title: 'Operating Margin',
    summary:
      'The share of each revenue dollar left after operating costs. Negative means the route loses money on the day shown.',
    formula: 'Operating margin = (Revenue − Operating cost) ÷ Revenue × 100 %',
    guidance: [
      'A negative margin with high load factor usually means fares are too low: raise them.',
      'A negative margin with low load factor usually means too much capacity: cut frequencies or downgauge.',
      'If the route is a feeder, judge it by the network yield it brings to trunk routes, not by its margin alone.',
    ],
  },
  capacitySpill: {
    title: 'Capacity Spill',
    summary:
      'Passengers who wanted to fly this route but were turned away because every seat was already sold.',
    formula: 'Capacity spill = Demand that found no available seat (pax)',
    guidance: [
      'Add a frequency at the busiest departure hour.',
      'Up-gauge the aircraft if the route is full on most days.',
      'Raise fares on the last seats to convert spill into higher yield rather than more passengers.',
    ],
  },
  priceLeakage: {
    title: 'Price Leakage',
    summary:
      'Passengers who chose a cheaper competitor or no trip at all because your fare was too high for their willingness to pay.',
    formula: 'Price leakage = Lost demand attributed to fare gap vs market baseline (pax)',
    guidance: [
      'Lower fares toward the market baseline on the cabin with the largest leak.',
      'If comfort is competitive, a small fare cut can recover passengers without a large yield loss.',
      'Cut operating costs instead of fares when the route already sells well.',
    ],
  },
  scheduleLeakage: {
    title: 'Schedule Leakage',
    summary:
      'Passengers lost because your departure times were poor (for example late-night or mid-afternoon gaps) or because connections had long layovers.',
    formula: 'Schedule leakage = Lost demand attributed to departure timing and connection layover (pax)',
    guidance: [
      'Retime the departure toward the morning or evening peak that competitors do not cover.',
      'Tighten hub connection banks so layovers stay short.',
      'Add a second departure in the gap that is losing the most passengers.',
    ],
  },
  comfortLeakage: {
    title: 'Comfort Leakage',
    summary:
      'Passengers lost to rivals with more generous seat pitch, Wi-Fi or in-seat power, or premium cabins.',
    formula: 'Comfort leakage = Lost demand attributed to comfort gap vs rivals (pax)',
    guidance: [
      'Increase seat pitch in economy, within the certified range, to raise the comfort score.',
      'Add Wi-Fi or in-seat power when the route competes with premium-heavy carriers.',
      'Add a premium cabin if comfort leakage is concentrated in high-yield business travellers.',
    ],
  },
  priceElasticity: {
    title: 'Price Elasticity Rating',
    summary:
      'How strongly demand responds to a change in fare for one cabin. The rating compares your proposed fare with the market baseline and estimates the demand change. "High price resistance" means passengers walk away quickly when the fare rises.',
    formula:
      'Demand change ≈ (Proposed fare ÷ Market baseline)^ε − 1, with ε < 0\n' +
      'LOW: change > −5 %   BALANCED: −20 % < change ≤ −5 %   HIGH: change ≤ −20 %',
    guidance: [
      'Price up cabins rated LOW first; they lose the fewest passengers per dollar of fare.',
      'On HIGH resistance cabins, hold fares at or below the market baseline and compete on comfort or timing.',
      'Re-check the rating after each fare change; it is estimated against the currently published fare as well.',
    ],
  },

  // ---------------------------------------------------------------------------
  // Fleet Rotation Gantt Grid
  // ---------------------------------------------------------------------------
  utilization: {
    title: 'Aircraft Utilization',
    summary:
      'The share of a day aircraft spend in the air, averaged across the fleet. Aircraft on the ground earn nothing, so higher utilization usually means more revenue per aircraft you own.',
    formula: 'Utilization % = Σ flying minutes ÷ (aircraft count × 1,440 min) × 100',
    guidance: [
      'Fill idle gaps in the grid with another leg if a route can support it.',
      'Move aircraft that sit idle for long stretches onto longer or busier routes.',
      'Do not chase 100 %: tight rotations leave no slack for delays or maintenance blocks.',
    ],
  },
  turnaroundBuffer: {
    title: 'Turnaround Buffer',
    summary:
      'The minimum ground time an aircraft must sit at an airport between landing and its next departure. Cleaning, refuelling, catering and boarding need this time.',
    formula: 'Earliest next departure = Arrival time + Type minimum turnaround (min)',
    guidance: [
      'Leave at least the type minimum between legs; the schedule flags a conflict when you do not.',
      'Give the buffer more room at outstations where you have limited ground staff.',
      'Turn aircraft faster only where the schedule has no slack; a late turn cascades into the next leg.',
    ],
  },
  maintenanceChecks: {
    title: 'A-Check / C-Check Thresholds',
    summary:
      'Scheduled maintenance is due after a set number of flight hours. The grid warns when an aircraft is close to a check, based on flight hours left and days of scheduled flying left.\n' +
      'A-Check: every 500 flight hours, 12 h downtime, no maintenance base needed. Warns with ≤ 50 h or < 7 days left.\n' +
      'C-Check: every 6,000 flight hours, 7 days downtime, needs a maintenance base. Warns with ≤ 300 h or < 30 days left.\n' +
      'D-Check: calendar-driven, 21 days downtime, needs a maintenance base. Scheduled manually.',
    formula:
      'Hours remaining = Interval − Flight hours since last check\n' +
      'Warning when Hours remaining ≤ warning window OR days of flying left < lookahead',
    guidance: [
      'Schedule the A-Check in a quiet overnight window before the warning reaches the aircraft.',
      'Book the C-Check at a maintenance base well ahead of the warning, since it takes seven days out of service.',
      'An OVERDUE status means the check is already past due; plan its downtime immediately.',
    ],
  },

  // ---------------------------------------------------------------------------
  // Cabin Layout Editor
  // ---------------------------------------------------------------------------
  floorSpaceUnits: {
    title: 'Floor Space Unit (FSU) Allocation',
    summary:
      'Every aircraft type has a fixed cabin floor budget in floor units. Each seat uses some of that budget, and a more generous seat uses more. The bar shows how the budget is split between classes.',
    formula:
      'Floor units used = Σ over classes (seats × floor units per seat)\n' +
      'Economy & Business: floor units per seat × (pitch ÷ default pitch)\n' +
      'Budget = type cabin floor units',
    guidance: [
      'Over budget means the layout cannot be certified: remove seats or reduce pitch until the bar is back under the marker.',
      'Unused floor units are wasted capacity; add seats or pitch to earn more or raise comfort.',
      'Business seats use several times the floor space of economy, so each premium seat costs multiple economy seats.',
    ],
  },
  seatPitch: {
    title: 'Seat Pitch and Comfort',
    summary:
      'Pitch is the distance between a seat and the one in front. Where it sits inside the class range sets part of the comfort score, which passengers weigh against fare.',
    formula:
      'Pitch score = (pitch − class min pitch) ÷ (class max pitch − class min pitch)\n' +
      'Comfort = 100 × clamp to 0–1 (base + pitch weight × pitch score + density weight × density score + amenity weight × amenity score)',
    guidance: [
      'Add pitch when comfort leakage is high and the cabin has floor units to spare.',
      'Pitch is not free: each extra inch of economy pitch uses more floor units and reduces seat count.',
      'Watch the density score: a cabin packed past half its floor budget loses comfort even at the same pitch.',
    ],
  },
  comfortMultiplier: {
    title: 'Comfort and Price Tolerance',
    summary:
      'The price tolerance multiplier scales how much of a fare premium passengers in a class will accept. Above 1.00× they tolerate a higher fare; below 1.00× they resist it.',
    formula:
      'Tolerance = clamp(1 + sensitivity × (comfort − expected comfort) ÷ 50, 0.6×, 1.6×)',
    guidance: [
      'Raise comfort in a class to lift its tolerance before you raise its fare.',
      'Each class has its own expected comfort, so compare the multiplier per class in the table before changing a cabin.',
      'A multiplier under 1.00× means the cabin will sell only at or below the market fare.',
    ],
  },
  massFuelBurn: {
    title: 'Mass Delta Fuel Burn',
    summary:
      'Heavier cabins burn more fuel. The fit-out weight is compared with the reference cabin the aircraft type was rated for; every kilogram over or under adds or saves fuel each flight hour.',
    formula:
      'Weight delta (kg) = Cabin fit-out weight − (floor units × 14 kg)\n' +
      'Effective burn (kg/hr) = Base burn + Weight delta (kg) × 0.03 kg/hr per kg\n' +
      '(Floored at a minimum fraction of base burn)',
    guidance: [
      'Remove Wi-Fi hardware or in-seat power on routes where the comfort gain does not earn its fuel cost.',
      'Use lighter seats or fewer galleys when the cabin is heavier than the reference.',
      'Compare fuel burn change against the extra revenue from comfort before committing to a refit.',
    ],
  },

  // ---------------------------------------------------------------------------
  // Market and Slot Auctions (topics ready for the auction view; not yet placed)
  // ---------------------------------------------------------------------------
  slotClearingPrice: {
    title: 'Slot Clearing Price',
    summary:
      'The price every winning bidder pays for a slot in a given hour. It is set by the highest losing bid for that hour, so winners pay the market-clearing rate rather than their own bid.',
    formula:
      'Clearing price = max(highest losing bid, reserve price), capped at each winner’s bid\n' +
      'Reserve price rises with hourly utilization; a fully booked hour costs the base slot value, and oversubscribed hours cost up to three times that.',
    guidance: [
      'Bid for peak hours only when the route’s yield can pay the clearing price over a year.',
      'Off-peak hours with spare capacity clear at almost nothing: use them to build frequency cheaply.',
      'A schedule must win every slot it needs, so one expensive hour can sink the whole rotation.',
    ],
  },
  grandfatherRights: {
    title: 'Grandfather Rights',
    summary:
      'Carriers that have operated at an airport before get a valuation boost on their slots. Incumbency makes it cheaper to defend a route than to win one new.',
    formula: 'Slot valuation = Route yield × 365 × profit share × (1 + 0.5 × historical presence) × …',
    guidance: [
      'Keep serving routes you already hold at a slot-constrained airport, even at thin margins, to protect the right.',
      'New entrants should target unconstrained airports or off-peak hours to build history.',
      'A route dropped for several seasons loses its incumbency premium; re-entry costs more.',
    ],
  },
  competitorYieldResistance: {
    title: 'Competitor Yield Resistance',
    summary:
      'How much a rival’s fares on a shared city pair limit the fare you can charge. High resistance means a rival is priced aggressively, so your price rises lose passengers quickly to them.',
    guidance: [
      'Check the rival’s fares before raising yield on a shared market; if resistance is high, compete on schedule or comfort instead.',
      'Compare your fare against the market baseline in the Ticket Pricing panel; a large gap signals a rival is setting the price.',
      'Resistance is a judgement, not a single number in the simulator yet: watch load factor and spill after each fare move.',
    ],
  },
} satisfies Record<string, InfoTopic>;

export type InfoTopicId = keyof typeof TOPICS;

export const INFO_TOPICS: Readonly<Record<InfoTopicId, InfoTopic>> = TOPICS;
